import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowLeftRight, ArrowUp, BarChart3, BookOpen, Bot, Briefcase, CalendarCheck, Calculator, Crown, FileText, Gauge, Landmark, LogOut, Menu, MessageCircle,
  Moon, Plus, ShieldCheck, Sun, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Icon, Logo, Spinner } from "@/components/kit";
import { FormDialog } from "@/components/ResourceTable";
import { Tour } from "@/components/Tour";
import { useAuth } from "@/context/AuthContext";
import { useWs } from "@/context/WorkspaceContext";
import { api, KIND_LABEL } from "@/lib/api";

const NAV = [
  ["Visão geral", "/app", Gauge], ["Lançamentos", "/app/lancamentos", ArrowLeftRight],
  ["Dívidas", "/app/dividas", Landmark], ["Patrimônio", "/app/patrimonio", Wallet], ["Planejamento", "/app/planejamento", TrendingUp],
  ["Metas & Tarefas", "/app/metas", CalendarCheck], ["Simuladores", "/app/simuladores", Calculator], ["Diagnóstico Empresarial", "/app/empresa", Briefcase],
  ["Mentor IA", "/app/mentor", Bot], ["Relatórios", "/app/relatorios", FileText], ["Assinatura", "/app/assinatura", Crown], ["Privacidade & LGPD", "/app/privacidade", ShieldCheck],
  ["Guia de uso", "/app/guia", BookOpen],
];

export const WS_FIELDS = [
  { name: "name", label: "Nome do espaço", default: "", wide: true },
  { name: "kind", label: "Tipo", type: "select", default: "pessoa", wide: true, options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })) },
];

function Nav({ onNav }) {
  const { user } = useAuth();
  const { ws } = useWs();
  const personal = ["pessoa", "familia"].includes(ws?.kind);
  const base = personal ? NAV.filter(([, to]) => to !== "/app/empresa") : NAV;
  const items = user?.role === "admin" ? [...base, ["Painel Admin", "/app/admin", BarChart3]] : base;
  return (
    <nav className="flex flex-col gap-0.5">
      {items.map(([label, to, I]) => (
        <NavLink key={to} to={to} end={to === "/app"} onClick={onNav} data-testid={`nav-${to.split("/").pop() || "dashboard"}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#D4AF37]/12 text-[#F5D77F] shadow-[inset_2px_0_0_#D4AF37]" : "text-slate-300 hover:bg-[#142C59] hover:text-white"}`}>
          <I className="h-4 w-4 shrink-0" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

function Sidebar({ onNav }) {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex h-full flex-col gap-6 p-4">
      <Logo className="mx-auto w-40 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,.45)]" testid="sidebar-logo" />
      <div className="flex-1 overflow-y-auto pr-1"><Nav onNav={onNav} /></div>
      <div className="rounded-xl border border-[#1E3A6E] bg-[#0B1A33] p-3">
        <div className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="truncate text-xs text-slate-400">{user?.email}</div>
        <button onClick={async () => { await signOut(); nav("/login"); }} className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-red-400" data-testid="logout-btn">
          <LogOut className="h-3.5 w-3.5" /> Sair
        </button>
      </div>
    </div>
  );
}

function PlanBanner() {
  const { ent } = useAuth();
  const nav = useNavigate();
  if (!ent || ent.status === "active") return null;
  const expired = ent.status === "expired";
  return (
    <div className={`no-print flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs md:px-8 ${expired ? "bg-red-500/15 text-red-300" : "bg-[#D4AF37]/10 text-[#F5D77F]"}`} data-testid="plan-banner">
      <span>{expired ? "Seu período de teste terminou. Assine um plano para continuar criando e usando o Mentor IA." : `Teste grátis ativo até ${new Date(ent.trial_ends_at).toLocaleDateString("pt-BR")}.`}</span>
      <button onClick={() => nav("/app/assinatura")} className="font-semibold underline underline-offset-2" data-testid="plan-banner-cta">Ver planos</button>
    </div>
  );
}

function ThemeToggle() {
  const [light, setLight] = useState(localStorage.getItem("pp_theme") === "light");
  useEffect(() => {
    document.documentElement.classList.toggle("light", light);
    localStorage.setItem("pp_theme", light ? "light" : "dark");
    return () => { document.documentElement.classList.remove("light"); };
  }, [light]);
  return (
    <Button size="sm" variant="outline" className="shrink-0 border-[#1E3A6E] bg-transparent" onClick={() => setLight((v) => !v)}
      title={light ? "Mudar para modo escuro" : "Mudar para modo claro"} aria-label="Alternar tema" data-testid="theme-toggle">
      {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
    </Button>
  );
}

function Topbar({ onMenu, onNew }) {
  const { list, wid, setWid } = useWs();
  const { ent } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/app"));
  return (
    <header className="glass no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#1E3A6E]/70 px-4 md:px-8">
      <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn" aria-label="Menu"><Menu className="h-6 w-6" /></button>
      {pathname !== "/app" && pathname !== "/app/" && (
        <Button size="sm" variant="outline" className="border-[#1E3A6E] bg-transparent text-[#F5D77F]" onClick={back} data-testid="page-back-btn" aria-label="Voltar">
          <ArrowLeft className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">Voltar</span>
        </Button>
      )}
      <Icon className="hidden h-8 w-8 sm:block lg:hidden" />
      <select value={wid || ""} onChange={(e) => setWid(e.target.value)} data-testid="workspace-switcher"
        className="h-10 min-w-0 max-w-[40vw] flex-1 rounded-lg border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] md:max-w-xs md:flex-none">
        {(list || []).map((w) => <option key={w.id} value={w.id}>{`${w.name} · ${KIND_LABEL[w.kind]}`}</option>)}
      </select>
      <Button size="sm" variant="outline" className="shrink-0 border-[#1E3A6E] bg-transparent" onClick={onNew} data-testid="new-workspace-btn">
        <Plus className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">Novo espaço</span>
      </Button>
      <div className="ml-auto flex items-center gap-2">
        <ThemeToggle />
        <span className="hidden rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#F5D77F] md:inline" data-testid="current-plan-badge">{ent?.name}</span>
      </div>
    </header>
  );
}

function ScrollTop() {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    const onScroll = () => { setShow(window.scrollY > 500); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); };
  }, []);
  if (!show) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Voltar ao topo" data-testid="scroll-top-btn"
      className="no-print fixed bottom-24 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37] text-[#0B1A33] shadow-xl transition-transform duration-200 hover:-translate-y-1">
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}

function Onboarding({ onNew }) {
  return (
    <div className="mx-auto max-w-xl py-20 text-center fade-up" data-testid="onboarding">
      <Logo className="mx-auto mb-8 w-56 rounded-2xl" testid="onboarding-logo" />
      <h1 className="text-3xl font-semibold">Bem-vindo ao PROSPERA+</h1>
      <p className="mt-3 text-slate-400">Crie seu primeiro espaço financeiro: pessoal, familiar, autônomo ou empresarial. Os dados de cada espaço ficam isolados.</p>
      <Button className="mt-8" onClick={onNew} data-testid="onboarding-create-btn"><Plus className="mr-1 h-4 w-4" /> Criar meu primeiro espaço</Button>
    </div>
  );
}

export default function AppShell() {
  const [menu, setMenu] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const { list, wid, ws, setWid, reload } = useWs();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const noWsOk = pathname.startsWith("/app/admin") || pathname.startsWith("/app/assinatura") || pathname.startsWith("/app/empresa");
  useEffect(() => {
    if (pathname.startsWith("/app/empresa") && ["pessoa", "familia"].includes(ws?.kind)) navigate("/app", { replace: true });
  }, [pathname, ws?.kind, navigate]);

  const createWs = async (p) => {
    const { data } = await api.post("/workspaces", p);
    await reload();
    setWid(data.id);
    toast.success("Espaço criado");
  };

  return (
    <div className="grain min-h-screen bg-[#060D1A]">
      <aside className="no-print fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-[#1E3A6E]/70 bg-[#0B1A33] lg:block"><Sidebar /></aside>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="w-72 border-[#1E3A6E] bg-[#0B1A33] p-0" data-testid="mobile-drawer"><Sidebar onNav={() => setMenu(false)} /></SheetContent>
      </Sheet>
      <div className="relative z-10 lg:pl-64">
        <Topbar onMenu={() => setMenu(true)} onNew={() => setNewOpen(true)} />
        <PlanBanner />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
          {list === null ? <Spinner /> : !wid && !noWsOk ? <Onboarding onNew={() => setNewOpen(true)} /> : <Outlet />}
        </main>
      </div>
      <ScrollTop />
      <a href="https://wa.me/5511996420785?text=Ol%C3%A1!%20Preciso%20de%20ajuda%20com%20o%20PROSPERA%2B" target="_blank" rel="noreferrer" aria-label="Ajuda pelo WhatsApp" data-testid="app-whatsapp-help-btn"
        className="no-print fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-2xl transition-transform duration-200 hover:-translate-y-1">
        <MessageCircle className="h-5 w-5" /><span className="hidden sm:inline">Ajuda</span>
      </a>
      {list?.length > 0 && <Tour />}
      <FormDialog open={newOpen} onOpenChange={setNewOpen} title="Novo espaço financeiro" fields={WS_FIELDS} onSubmit={createWs} testid="workspace-dialog" />
    </div>
  );
}
